import { Component, Inject, InjectionToken, Injector, OnInit, Optional, Type } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogTitle, MatDialogContent, MatDialogActions } from '@angular/material/dialog';
import { SELECT_DIALOG_SERVICE } from '@core/data/injection-tokens';
import { SelectForm } from '@shared/models/forms/select.form';
import { FilterService } from '@core/models/form/filter-service';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PagedSelectOptions, PaginationFilterSettings } from '@antony/ng-forms/core';
import { NgIf } from '@angular/common';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { FormComponent } from '@antony/ng-forms/material';
import { AntButtonModule } from '@antony/ng-ui';
import { TranslatePipe } from '@ngx-translate/core';

export interface SelectDialogOptions<T> {
  title?: string;
  injectionToken?: InjectionToken<FilterService<T>> | Type<FilterService<T>>;
  defaultValue?: T | null;
}

@Component({
  selector: 'app-select-dialog',
  templateUrl: './select-dialog.component.html',
  styleUrls: ['./select-dialog.component.scss'],
  imports: [MatDialogTitle, NgIf, CdkScrollable, MatDialogContent, MatProgressSpinner, FormComponent, MatDialogActions, AntButtonModule, TranslatePipe],
})
export class SelectDialogComponent<T extends {}> implements OnInit {
  form: SelectForm<T> | null = null;
  private dialogService: FilterService<T> | null = null;

  constructor(
    @Optional()
    public dialogRef: MatDialogRef<SelectDialogComponent<T>, T>,
    @Optional()
    @Inject(MAT_DIALOG_DATA)
    public config: SelectDialogOptions<T>,
    private injector: Injector
  ) {}

  ngOnInit() {
    this.dialogService = this.injector.get<FilterService<T> | null>(!!this.config?.injectionToken ? this.config?.injectionToken : SELECT_DIALOG_SERVICE, null);
    if (!this.dialogService) {
      return;
    }
    this.form = new SelectForm<T>({
      data: {
        loadPagedFilteredOptions: (settings) => this.filterData(settings),
        getTriggerLabel: (val: T | null | undefined) => this.dialogService?.getLabelFromValue(val) || '',
        value: this.config?.defaultValue ?? undefined,
      },
    });
  }

  submit() {
    if (!this.form) {
      return;
    }
    this.dialogRef.close(this.form.value?.data);
  }

  abort() {
    this.dialogRef.close();
  }

  private filterData(settings: PaginationFilterSettings): Observable<PagedSelectOptions<T>> {
    return this.dialogService!.filter(settings.filterValue).pipe(
      map((res) => ({
        data: res ?? [],
        maxCount: res.length,
      }))
    );
  }
}
